import { APP_BASE_HREF } from '@angular/common'
import { Injectable, inject } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { Store } from '@ngrx/store'

import { Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'

import { DataMappingType } from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import { PaginationQueryParams } from '@flowx/designer/shared/api/api-http.service'
import {
  PaginatedEndpoint,
  PaginatedResponse,
  Sort,
} from '@flowx/designer/shared/services/paginated-datasource'

import { GridSearchService } from './grid-search.service'

import { UiFlowsApiService } from '../api/ui-flows.api'
import { ApplicationResource, mapResource } from '../entities/application.entity'
import { UiFlow } from '../entities/ui-flow.entity'
import { selectWorkspaceId } from '../store/selectors/router.selectors'

const SORT_FIELD = 'modifiedDate'
const SORT_DIRECTION = 'desc'

@Injectable()
export class UiFlowsService extends GridSearchService<UiFlow> {
  #route = inject(ActivatedRoute)
  #router = inject(Router)
  #uiFlowsApi = inject(UiFlowsApiService)
  #baseHref = inject(APP_BASE_HREF)
  #store = inject(Store)

  getGridEndpointInfo(): {
    endpoint: PaginatedEndpoint<any, UiFlow>
    sort: Sort
    query: Partial<any>
  } {
    return {
      endpoint: (request, query) =>
        this.getUiFlowsList({
          ...request,
          ...query,
          latest: true,
        }),
      sort: {
        property: SORT_FIELD,
        direction: SORT_DIRECTION,
      },
      query: { name: this.#route.snapshot.queryParams?.searchQuery ?? '' },
    }
  }

  getUiFlowsList(
    queryParams: PaginationQueryParams & { latest: boolean }
  ): Observable<PaginatedResponse<UiFlow>> {
    return this.#uiFlowsApi
      .getAllUiFlows(queryParams)
      .pipe(map((response) => mapResource(response)))
  }

  createUiFlow(uiFlow: Partial<UiFlow>): Observable<UiFlow> {
    return this.#uiFlowsApi
      .createUiFlow({
        name: uiFlow.name,
        platform: uiFlow.platform,
        description: uiFlow.description,
        appId: uiFlow.appId,
        templateConfig: uiFlow.templateConfig,
        type: uiFlow.type,
        chatWorkflow: uiFlow.chatWorkflow,
      })
      .pipe(
        map((response) => mapResource(response)),
        tap(() => this.refresh())
      )
  }

  updateUiFlow(uiFlow: Partial<UiFlow>): Observable<UiFlow> {
    return this.#uiFlowsApi
      .updateUiFlow(
        {
          name: uiFlow.name,
          platform: uiFlow.platform,
          description: uiFlow.description,
          appId: uiFlow.appId,
          type: uiFlow.type,
          chatWorkflow: uiFlow.chatWorkflow,
          allowedRoles: uiFlow.allowedRoles,
          expiryTime: uiFlow.expiryTime,
        },
        uiFlow.resourceId
      )
      .pipe(tap(() => this.refresh()))
  }

  deleteUiFlow(uiFlow: UiFlow): Observable<void> {
    return this.#uiFlowsApi.deleteUiFlow(uiFlow.resourceId).pipe(tap(() => this.refresh()))
  }

  clearCache(uiFlow: UiFlow): Observable<void> {
    return this.#uiFlowsApi.clearAllCachesUiFlow(uiFlow.resourceId)
  }

  getUiFlowById(resourceId: string): Observable<UiFlow> {
    return this.#uiFlowsApi.getUiFlowById(resourceId).pipe(map((response) => mapResource(response)))
  }

  getUiFlow(resourceId: string): Observable<UiFlow> {
    return this.#uiFlowsApi.getUiFlow(resourceId).pipe(map((response) => mapResource(response)))
  }

  goToDataModelParameters(resource: ApplicationResource, type: DataMappingType): void {
    const { appId, appVersionId, resourceId } = resource
    const workspaceId = this.#store.selectSignal(selectWorkspaceId)()
    const link = this.#router.serializeUrl(
      this.#router.createUrlTree(
        [
          this.#baseHref,
          'guard',
          'workspace',
          workspaceId,
          'projects',
          appId,
          'config',
          appVersionId,
          'ui-flows',
          resourceId,
          'data-model',
        ],
        {
          queryParams: {
            tabIndex: type === DataMappingType.INPUT ? 1 : 2,
          },
        }
      )
    )
    window.open(link, '_blank')
  }
}
